import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import WhatsappClient from '../WhatsappClient';
import { useGetClientById, useGetWhatsappClientMessages } from '@frontend/features/clients/queries';
import { ClientResponseDto, WhatsappRecord } from '@bandido/shared/dtos';
import { useWS } from '@frontend/features/common/hooks/useWS';
import { CONFIG } from '@frontend/config';
import { WsMessages } from '@bandido/shared/constants';
import { WhatsappWebSocketsEvents } from '@bandido/shared/types';
import { useCopilotSuggestions } from '@frontend/features/copilot/hooks/useCopilotSuggestions';
import { MessageResponse } from '../../types';
import { client } from '@frontend/features/common/services';
import { useDebouncedMessageUpdates } from '@frontend/features/common/hooks/useDebouncedMessageUpdates';
import { useQueryClient } from 'react-query';
import { QueryKeys } from '@frontend/features/common/types';
import { escapeFilename } from '@frontend/features/common/utils/helpers';
import { matchPath, useLocation } from 'react-router-dom';

export interface WhatsappClientContextType {
  isOpen: boolean;
  toggleIsOpen: () => void;
  openWhatsapp: (clientId?: string) => void;
  clientData?: ClientResponseDto;
  clientDataIsloading?: boolean;
  messages: MessageResponse[];
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  open: () => void;
  close: () => void;
  handleSendTextMessage: (value: string) => void;
  handleSendAudioMessage: (audioBlob: Blob, clientId?: string) => void;
  handleSendMediaMessage: (
    agencyId: number,
    mediaBlob: Blob,
    clientId?: string,
    filename?: string,
    caption?: string,
  ) => Promise<void>;
  currentCaseId?: number;
  lastClientMessage: string | null;
  isSessionExpired: boolean;
  setIsSessionExpired: (value: boolean) => void;
  handleLinkMessageToCase: (messageId: string, caseId: number) => Promise<void>;
  handleUnlinkMessageFromCase: (messageId: string, caseId: number) => Promise<void>;
  isSending: boolean;
  messageToReply: WhatsappRecord | null;
  setMessageToReply: (value: WhatsappRecord | null) => void;
}

interface Props {
  children: React.ReactNode;
}

const getLastInboundCreatedAt = (data: MessageResponse[]): string | null => {
  const sorted = [...data].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const lastInbound = sorted.reverse().find((item) => item.inbound);
  return lastInbound?.createdAt ?? null;
};

const WhatsappClientContext = createContext<WhatsappClientContextType | undefined>(undefined);

export const WhatsappClientProvider = ({ children }: Props) => {
  const queryClient = useQueryClient();
  const { isConnected, socketRef, sendMessage, joinRoom, leaveRoom } = useWS(`${CONFIG.WS_URL}/whatsapp`);
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [clientId, setClientId] = useState<string>();
  const [messageToReply, setMessageToReply] = useState<WhatsappRecord | null>(null);
  const [isSending, setIsSending] = useState(false);
  const clientIdRef = useRef<string>();
  const joinedRoomRef = useRef<string>();
  const joinedSocketIdRef = useRef<string>();
  const registeredSocketIdRef = useRef<string>();
  const location = useLocation();
  const { data: clientData, isLoading: clientDataIsloading } = useGetClientById({
    id: clientId,
  });
  const [lastClientMessage, setLastClientMessage] = useState<string | null>(null);

  const { scheduleUpdate, getPendingUpdates, updateTrigger } = useDebouncedMessageUpdates<MessageResponse>(300);

  const { data, isFetchingNextPage, fetchNextPage, hasNextPage } = useGetWhatsappClientMessages(clientId, {
    page: 1,
    limit: 50,
  });

  const currentCaseId = useMemo(() => {
    const match = matchPath('/dashboard/cases/:id', location.pathname);
    const rawId = match?.params?.id;
    if (!rawId) return undefined;
    const parsed = Number(rawId);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, [location.pathname]);

  const messages = useMemo(() => data?.pages.map((page) => page.data.items).flat() || [], [data]);

  useEffect(() => {
    if (updateTrigger > 0) {
      const updates = getPendingUpdates();
      if (updates.length > 0) {
        queryClient.invalidateQueries([QueryKeys.getWhatsappClientMessages, { clientId }]);
      }
    }
  }, [updateTrigger, getPendingUpdates, queryClient]);

  useEffect(() => {
    setLastClientMessage(getLastInboundCreatedAt(messages));
  }, [messages]);

  useEffect(() => {
    if (isSending && messages.length > 0) {
      setIsSending(false);
    }
  }, [messages, isSending]);

  const toggleIsOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const openWhatsapp = useCallback(
    (nextClientId?: string) => {
      clientIdRef.current = nextClientId;

      setClientId((prev) => {
        if (prev && prev !== nextClientId) {
          const previousRoomId = `messages-${prev}`;

          setTimeout(() => {
            if (clientIdRef.current !== prev && socketRef.current?.connected) {
              leaveRoom(previousRoomId);
            }
          }, 2000);
        }

        if (prev === nextClientId) {
          joinedRoomRef.current = prev ? `messages-${prev}` : undefined;
          joinedSocketIdRef.current = socketRef.current?.id;
        } else if (prev) {
          const previousRoomId = `messages-${prev}`;

          if (joinedRoomRef.current === previousRoomId) {
            joinedRoomRef.current = undefined;
          }

          if (joinedSocketIdRef.current === socketRef.current?.id) {
            joinedSocketIdRef.current = undefined;
          }
        }

        return nextClientId;
      });

      toggleIsOpen();
    },
    [leaveRoom, socketRef, toggleIsOpen],
  );

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const {
    suggestions: copilotSuggestions,
    dismiss: copilotDismiss,
    clearReplies: copilotClearReplies,
    requestReply: copilotRequestReply,
    requestFacet: copilotRequestFacet,
    isConnected: copilotIsConnected,
  } = useCopilotSuggestions(currentCaseId);

  const handleSendTextMessage = useCallback(
    async (textBody: string) => {
      if (clientId && isConnected) {
        setIsSending(true);
        try {
          sendMessage(
            WsMessages.sendWhatsappTextMesage,
            JSON.stringify({
              clientId,
              textBody,
              replyingToWpId: messageToReply?.wpId || null,
              caseId: currentCaseId ?? null,
            }),
          );
          setMessageToReply(null);
          // El agente respondió: las respuestas sugeridas quedaron obsoletas.
          copilotClearReplies();
        } catch (_e) {
          console.log('There was an error WhatsappClientContext handleSendTextMessage');
          setIsSending(false);
        }
      }
    },
    [
      clientId,
      messageToReply,
      isConnected,
      currentCaseId,
      copilotClearReplies,
    ],
  );

  const handleSendMediaMessage = useCallback(
    async (agencyId: number, mediaBlob: Blob, clientId?: string, filename?: string, caption?: string) => {
      if (clientId && isConnected) {
        setIsSending(true);
        try {
          const formData = new FormData();
          formData.append('file', mediaBlob, filename ? escapeFilename(filename) : 'media.file');
          formData.append('clientId', clientId);
          formData.append('replyingToWpId', messageToReply?.wpId || 'null');
          if (currentCaseId) {
            formData.append('caseId', String(currentCaseId));
          }

          if (caption) {
            formData.append('caption', caption);
          }

          await client.post(`/whatsapp/send-message-file/${agencyId}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          setMessageToReply(null);
        } catch (_e) {
          console.log('There was an error WhatsappClientContext handleSendMediaMessage', _e);
          setIsSending(false);
        }
      }
    },
    [clientId, messageToReply, isConnected, currentCaseId],
  );

  const handleSendAudioMessage = useCallback(
    async (audioBlob: Blob, clientId?: string) => {
      if (clientData?.agencyId) {
        setIsSending(true);
        return handleSendMediaMessage(clientData?.agencyId, audioBlob, clientId, 'audio.wav');
      }
    },
    [handleSendMediaMessage, clientData?.agencyId],
  );

  const handleLinkMessageToCase = useCallback(
    async (messageId: string, caseId: number) => {
      try {
        await client.post('/case-history/whatsapp', {
          caseId,
          whatsappMsgId: messageId,
        });

        queryClient.invalidateQueries([QueryKeys.getWhatsappClientMessages, { clientId }]);
      } catch (error) {
        console.error('Error al vincular el mensaje con el caso:', error);
      }
    },
    [queryClient, clientId],
  );

  const handleUnlinkMessageFromCase = useCallback(
    async (messageId: string, caseId: number) => {
      try {
        await client.delete(`/case-history/whatsapp/${messageId}/case/${caseId}`);
        queryClient.invalidateQueries([QueryKeys.getWhatsappClientMessages, { clientId }]);
      } catch (error) {
        console.error('Error al desvincular el mensaje del caso:', error);
      }
    },
    [queryClient, clientId],
  );

  useEffect(() => {
    const socket = socketRef.current;

    if (!socket?.id || !isConnected) {
      registeredSocketIdRef.current = undefined;
      joinedRoomRef.current = undefined;
      joinedSocketIdRef.current = undefined;
      return;
    }

    if (registeredSocketIdRef.current !== socket.id) {
      sendMessage(WsMessages.registerClient, socket.id);
      registeredSocketIdRef.current = socket.id;
    }

    const handleMessage = (msg: string) => {
      try {
        const parsed = JSON.parse(msg);

        const queries = queryClient.getQueryCache().findAll({ queryKey: [QueryKeys.getWhatsappClientMessages, { clientId }] });

        queries.forEach(({ queryKey }) => {
          queryClient.setQueryData(queryKey, (oldData: any) => {
            if (!oldData?.pages) return oldData;

            const pages = [...oldData.pages];
            const items = [...pages[pages.length - 1].data.items];
            const parsedAlreadyExists = pages[pages.length - 1].data.items.find((item: any) => item.id === parsed.id);

            if (!parsedAlreadyExists) {
              items.push(parsed);
            }

            pages[pages.length - 1].data = { ...pages[pages.length - 1].data, items };

            if (oldData) {
              return {
                ...oldData,
                pages,
              };
            }
          });
        });
      } catch (e) {
        console.error('Error al parsear el mensaje WS', e);
      }
    };

    const handleMessageUpdate = (msg: string) => {
      try {
        const parsed = JSON.parse(msg);

        // Usar debounce para actualizaciones
        scheduleUpdate(parsed);
      } catch (e) {
        console.error('Error al parsear la actualización de mensaje WS', e);
      }
    };

    socket.on('message', handleMessage);
    socket.on(WhatsappWebSocketsEvents.MESSAGE_UPDATE, handleMessageUpdate);

    if (clientId) {
      const roomId = `messages-${clientId}`;

      if (joinedRoomRef.current !== roomId || joinedSocketIdRef.current !== socket.id) {
        setIsSessionExpired(true);
        joinRoom(roomId);
        joinedRoomRef.current = roomId;
        joinedSocketIdRef.current = socket.id;
      }
    }

    return () => {
      socket.off('message', handleMessage);
      socket.off(WhatsappWebSocketsEvents.MESSAGE_UPDATE, handleMessageUpdate);
    };
  }, [clientId, isConnected, joinRoom, queryClient, scheduleUpdate, sendMessage, socketRef]);


  return (
    <WhatsappClientContext.Provider
      value={{
        isSessionExpired,
        setIsSessionExpired,
        lastClientMessage,
        isOpen,
        toggleIsOpen,
        open,
        close,
        openWhatsapp,
        clientData,
        clientDataIsloading,
        handleSendTextMessage,
        handleSendAudioMessage,
        handleSendMediaMessage,
        currentCaseId,
        messages,
        handleLinkMessageToCase,
        handleUnlinkMessageFromCase,
        isSending,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage: hasNextPage || false,
        messageToReply,
        setMessageToReply,
      }}
    >
      {children}
      <WhatsappClient
        open={isOpen}
        onClose={close}
        copilotSuggestions={copilotSuggestions}
        copilotDismiss={copilotDismiss}
        copilotRequestReply={copilotRequestReply}
        copilotRequestFacet={copilotRequestFacet}
        copilotIsConnected={copilotIsConnected}
      />
    </WhatsappClientContext.Provider>
  );
};

export const useWhatsappClient = (): WhatsappClientContextType => {
  const context = useContext(WhatsappClientContext);
  if (!context) {
    throw new Error('useWhatsappClient debe usarse dentro de un WhatsappClientProvider');
  }
  return context;
};

export const useOptionalWhatsappClient = (): WhatsappClientContextType | undefined =>
  useContext(WhatsappClientContext);
